import { t } from "@lingui/core/macro";
import { Link } from "@tanstack/react-router";

export type Article = {
  slug: string;
  title: string;
  description: string;
  date: string;
  readTime: string;
  tag: string;
  body: React.ReactNode;
  /** Optional Q&A pairs. Rendered as a visible FAQ and emitted as FAQPage JSON-LD. */
  faq?: { q: string; a: string }[];
};

export const getArticles = (): Article[] => [
  {
    slug: "resume-summary-examples",
    title: t`Resume Summary Examples: 15+ Templates by Job & Level`,
    description: t`Browse 15+ resume summary examples by job and experience level - including no-experience samples - plus a simple formula and fill-in-the-blank templates.`,
    date: "2026-07-02",
    readTime: t`9 min read`,
    tag: t`Examples`,
    body: (
      <>
        <p>
          {t`A resume summary is a two-to-four-sentence pitch at the top of your resume that tells a recruiter who you are, what you're best at, and why you fit the role - before they read anything else. The fastest way to write a good one is to start from an example that matches your situation and adapt it. Below are 15+ resume summary examples grouped by experience level and industry, a reusable formula, copy-and-edit templates, and a before-and-after rewrite.`}
        </p>
        <p>
          {t`Prefer the full method behind these samples? Read our companion guide:`}{" "}
          <Link to="/articles/$slug" params={{ slug: "resume-summary-guide" }}>
            {t`How to Write a Resume Summary That Gets You Noticed`}
          </Link>
        </p>

        <h2>{t`The formula behind every strong summary`}</h2>
        <p>
          {t`Almost every effective summary follows the same shape. Fill in these four slots and you have a working draft:`}
        </p>
        <ul>
          <li>{t`Who you are - your title, seniority, and years of experience ("Marketing manager with 6 years in B2B SaaS").`}</li>
          <li>{t`What you're known for - your signature strength or specialization ("specializing in demand generation and lifecycle email").`}</li>
          <li>{t`Proof - one quantified achievement that backs up the claim ("grew qualified pipeline 40% in a year").`}</li>
          <li>{t`The bridge - what you want to do for this employer, echoing the job title ("looking to drive growth for an early-stage product team").`}</li>
        </ul>

        <h2>{t`Resume summary examples by experience level`}</h2>

        <h3>{t`Entry-level / recent graduate`}</h3>
        <p>
          {t`"Recent computer science graduate with internship experience building React web apps and a 3-month co-op shipping features used by 10k+ users. Comfortable across the JavaScript stack and eager to grow as a front-end engineer on a collaborative product team."`}
        </p>
        <p>
          {t`"Marketing graduate with hands-on experience from two internships, where I ran social campaigns that grew engagement 35%. Strong in content creation and analytics, seeking an entry-level digital marketing role to turn data into campaigns that convert."`}
        </p>

        <h3>{t`Mid-career professional`}</h3>
        <p>
          {t`"Project manager with 7 years delivering software projects for financial-services clients, consistently on time and under budget. Certified Scrum Master who has led cross-functional teams of up to 15. Looking to bring a delivery-focused, stakeholder-first approach to a scaling product organization."`}
        </p>
        <p>
          {t`"Registered nurse with 6 years in high-volume emergency and med-surg units, known for calm triage under pressure and a 98% patient-satisfaction score. Seeking a charge-nurse role where clinical judgment and mentoring newer staff make the biggest difference."`}
        </p>

        <h3>{t`Senior / leadership`}</h3>
        <p>
          {t`"Senior product manager with 10+ years in B2B SaaS, specializing in turning ambiguous market opportunities into shipped products with measurable retention impact. Led 0-to-1 launches across fintech and HR-tech, including one that reached $4M ARR in 18 months. Ready to own the enterprise roadmap for a mission-driven team."`}
        </p>
        <p>
          {t`"Engineering leader with 12 years building and scaling teams from 3 to 40 engineers. Shipped platforms serving millions of daily users while cutting infrastructure cost 30%. Seeking a VP Engineering role where technical strategy and people development go hand in hand."`}
        </p>

        <h2>{t`Resume summary examples by industry`}</h2>
        <ul>
          <li>{t`Software engineer - "Full-stack engineer with 5 years in TypeScript, React, and Node, focused on performance and clean architecture. Cut page-load time 45% on a flagship app serving 200k monthly users."`}</li>
          <li>{t`Sales - "B2B account executive with 4 years closing mid-market SaaS deals, 128% of quota last year. Skilled at multi-threaded enterprise sales and turning cold pipeline into six-figure contracts."`}</li>
          <li>{t`Customer support - "Customer success specialist with 3 years supporting SaaS accounts, maintaining a 95% CSAT and reducing churn 12% through proactive onboarding."`}</li>
          <li>{t`Accounting - "Staff accountant with 5 years in month-end close, reconciliations, and audit prep for mid-sized firms. Reduced close time from 10 days to 6 through process automation."`}</li>
          <li>{t`Teaching - "High school science teacher with 8 years raising standardized-test pass rates 20% through hands-on, inquiry-based lessons. Skilled at differentiating instruction for mixed-ability classrooms."`}</li>
          <li>{t`Administrative - "Executive assistant with 6 years supporting C-level leaders, managing complex calendars, travel, and board prep across time zones with zero missed deadlines."`}</li>
        </ul>

        <h2>{t`Resume summary examples with no experience`}</h2>
        <p>
          {t`No formal work history yet? Lead with education, projects, transferable skills, and enthusiasm framed around the employer's needs - not your own.`}
        </p>
        <p>
          {t`"Detail-oriented business student (graduating May 2026) with coursework in data analysis and a class project forecasting sales for a local retailer. Quick learner comfortable with Excel and SQL, seeking an entry-level analyst role to apply analytical skills to real business problems."`}
        </p>
        <p>
          {t`"Reliable and customer-focused recent high school graduate with volunteer experience coordinating a 200-person community event. Strong communicator and fast learner looking for a first retail role where dependability and a positive attitude add value from day one."`}
        </p>
        <p>
          {t`"Career changer moving from hospitality into UX design, with a completed 6-month design bootcamp and three portfolio projects. Bring 5 years of reading customer needs under pressure - now applied to research-driven, user-first design."`}
        </p>

        <h2>{t`Fill-in-the-blank templates`}</h2>
        <p>{t`Copy a template and swap the brackets for your own details:`}</p>
        <ul>
          <li>{t`Experienced - "[Job title] with [X] years in [industry/domain], specializing in [signature skill]. [Quantified achievement]. Seeking to [what you'll do] for [type of team/company]."`}</li>
          <li>{t`Entry-level - "[Degree/field] graduate with [internship/project experience] in [skill area]. [A result or highlight]. Looking for an entry-level [role] to [contribution you'll make]."`}</li>
          <li>{t`Career change - "[Current role] with [X] years of [transferable strength], transitioning into [new field] after [training/proof]. Bringing [asset] to [new role]."`}</li>
        </ul>

        <h2>{t`Before and after: fixing a weak summary`}</h2>
        <p>
          {t`Before: "Hardworking and results-driven professional with excellent communication skills looking for a challenging opportunity to grow my career." This says nothing specific, focuses on what the candidate wants, and could belong to anyone.`}
        </p>
        <p>
          {t`After: "Operations coordinator with 4 years streamlining logistics for e-commerce brands, cutting fulfillment errors 30% and shipping costs 15%. Looking to bring that efficiency mindset to a fast-scaling DTC team." Now it's concrete, quantified, and aimed squarely at the role.`}
        </p>

        <h2>{t`Common mistakes to avoid`}</h2>
        <ul>
          <li>{t`Writing an objective instead of a summary - focus on what you offer, not what you want.`}</li>
          <li>{t`Leading with "I" - use third-person fragments ("Experienced designer…" not "I am an experienced designer…").`}</li>
          <li>{t`Stacking soft-skill clichés - "team player, hard worker" prove nothing; show results instead.`}</li>
          <li>{t`Reusing one summary everywhere - adjust the bridge sentence to echo each job's title and top requirement.`}</li>
          <li>{t`Padding past four sentences - the summary should point to your evidence, not replace it.`}</li>
        </ul>

        <p>
          {t`Once you've picked and adapted an example, ResumeLabs keeps the rest consistent - clean, ATS-friendly formatting and a live preview - so your polished summary lands at the top of a resume that reads just as well below it.`}
        </p>
      </>
    ),
    faq: [
      {
        q: t`How long should a resume summary be?`,
        a: t`Two to four sentences - roughly 40 to 60 words. Long enough to state who you are, your strongest skill, and a quantified result, but short enough that a recruiter reads it in their first few seconds. If it runs past four sentences, move the detail into your experience bullets.`,
      },
      {
        q: t`Should a resume summary be written in first person?`,
        a: t`Write it in third-person fragments and drop the "I". Say "Marketing manager with 6 years in B2B SaaS…" rather than "I am a marketing manager…". It reads as more professional, saves words, and matches the convention recruiters expect at the top of a resume.`,
      },
      {
        q: t`Do I need a summary if I have no experience?`,
        a: t`Yes - it's especially useful with no experience, because it lets you lead with education, projects, transferable skills, and enthusiasm before a recruiter reaches a thin work-history section. Frame everything around what you can do for the employer, and mention relevant coursework, internships, or volunteer work as proof.`,
      },
      {
        q: t`What's the difference between a resume summary and an objective?`,
        a: t`A summary describes what you offer - your experience, skills, and results. An objective describes what you want from the employer. Recruiters care about the former, so a summary almost always outperforms an objective. Use a brief bridge sentence to signal the role you're targeting instead of a separate objective.`,
      },
    ],
  },
  {
    slug: "ats-resume-guide",
    title: t`ATS Resume Guide – How to Pass Screening Systems`,
    description: t`A deep-dive on how Applicant Tracking Systems work, what causes good resumes to fail them, and the exact techniques you need to get your resume in front of a human recruiter.`,
    date: "2026-03-20",
    readTime: t`9 min read`,
    tag: t`ATS`,
    body: (
      <>
        <p>
          {t`Applicant Tracking Systems (ATS) were originally built to organize large volumes of applications. Today they act as a de facto first screen: your resume must satisfy the algorithm before any human reads it. This guide explains how these systems work mechanically and gives you a practical checklist to pass them every time.`}
        </p>

        <h2>{t`How ATS parsing actually works`}</h2>
        <p>
          {t`When you upload a resume, the ATS extracts raw text and maps it onto a structured profile - name, contact info, job titles, employers, dates, skills, education. It then scores that profile against the job requisition using keyword matching, required qualifications, and sometimes machine-learning rankings. A low score means automatic rejection or deprioritization, regardless of how qualified you actually are.`}
        </p>
        <p>
          {t`Parsing quality varies widely between systems. Enterprise platforms like Workday, Greenhouse, Lever, iCIMS, and Taleo each have their own quirks. The safest strategy is to optimize for the lowest common denominator: plain, well-structured text.`}
        </p>

        <h2>{t`The seven most common ATS failure points`}</h2>
        <p>
          {t`Understanding why resumes fail is more useful than vague advice to "use keywords". Here are the concrete failure modes:`}
        </p>
        <ul>
          <li>{t`Complex layouts - Multi-column designs, text boxes, and tables cause the parser to read content in the wrong order or skip it entirely.`}</li>
          <li>{t`Headers and footers - Many systems cannot extract text placed in a document's header or footer. If your phone number is only in the footer, it may be invisible to the ATS.`}</li>
          <li>{t`Images and graphics - Icons, logos, and photo headshots are simply ignored. Never put contact information inside an image.`}</li>
          <li>{t`Non-standard fonts - Custom or embedded fonts sometimes render as garbage characters after text extraction. Use common system fonts.`}</li>
          <li>{t`Creative section headings - "My Story" and "What I Bring" confuse the parser. Use "Experience", "Education", "Skills", "Summary".`}</li>
          <li>{t`Abbreviations without expansion - If a job description says "Search Engine Optimization" but your resume only says "SEO", you may miss the match. Use both on first mention.`}</li>
          <li>{t`Inconsistent date formats - Mixing "Jan 2022", "01/2022", and "2022-01" can cause the parser to misread your timeline. Pick one format and stick to it.`}</li>
        </ul>

        <h2>{t`Keyword strategy: quality over quantity`}</h2>
        <p>
          {t`Keyword stuffing - hiding dozens of terms in white text or cramming them into a wall of text - worked briefly years ago and is now actively penalized by modern systems. The right approach is surgical relevance.`}
        </p>
        <p>
          {t`For each application, copy the job description into a text editor and highlight every skill, tool, certification, and qualification mentioned. Then audit your resume: where you genuinely have that experience, use the exact phrasing from the job posting. "Leading cross-functional teams" is different from "managing multi-department projects" even if they describe the same thing.`}
        </p>
        <p>
          {t`Pay particular attention to the required qualifications section. These are the hard gates - the system is explicitly scoring for them. If you meet a requirement, make sure it appears verbatim somewhere on your resume.`}
        </p>

        <h2>{t`File format: PDF vs DOCX`}</h2>
        <p>
          {t`The PDF vs DOCX debate depends on the system. Modern ATS platforms (post-2020) generally parse both well. However, PDF is safer when: (a) you care deeply about layout preservation, (b) you are applying through a web form that accepts both, or (c) the job posting doesn't specify.`}
        </p>
        <p>
          {t`One edge case: some older or government-sector ATS systems explicitly prefer DOCX. If the application portal says "Word document preferred", obey it. ResumeLabs can export a clean DOCX from the same data you use for PDF.`}
        </p>

        <h2>{t`Testing your resume before you apply`}</h2>
        <p>
          {t`Before submitting, run a quick self-audit: paste your resume text (not the PDF - the extracted text) into a plain text editor. What you see is roughly what the ATS sees. Does your contact info appear? Are job titles and dates readable? Do your key skills appear in a logical context?`}
        </p>
        <p>
          {t`Free tools like Jobscan and Resume Worded let you upload your resume alongside a job description and receive an ATS match score. They're imperfect simulators, but they catch obvious gaps quickly.`}
        </p>

        <h2>{t`The human read still matters`}</h2>
        <p>
          {t`ATS optimization gets your resume past the gate - but a human recruiter decides whether to advance you. Don't let keyword optimization make your resume robotic or hollow. Every bullet point should convey a real accomplishment, and your summary should read naturally. The goal is a resume that impresses both the algorithm and the person behind it.`}
        </p>
      </>
    ),
  },
  {
    slug: "resume-summary-guide",
    title: t`How to Write a Resume Summary That Gets You Noticed`,
    description: t`The professional summary is the first thing recruiters read. Learn the formula for writing a concise, compelling opener that makes them want to keep reading.`,
    date: "2026-03-10",
    readTime: t`7 min read`,
    tag: t`Writing`,
    body: (
      <>
        <p>
          {t`The professional summary sits at the top of your resume - prime real estate. A recruiter spending six seconds on an initial scan will almost certainly read it. Yet most summaries are either vague ("results-driven professional with excellent communication skills") or non-existent. Done well, a summary anchors your entire application to the specific role you want.`}
        </p>

        <h2>{t`What a summary is not`}</h2>
        <p>
          {t`It's not an objective statement. Objective statements ("Seeking a challenging role where I can grow…") focus on what you want from the employer. A summary focuses on what you offer. Recruiters don't care what you're seeking - they care whether you can solve their problem.`}
        </p>
        <p>
          {t`It's also not a list of soft skills. "Excellent communicator, team player, detail-oriented" tells a recruiter nothing differentiating. Every candidate says the same thing. Soft skills belong in context - embedded in bullet points that demonstrate them through action.`}
        </p>

        <h2>{t`The three-part formula`}</h2>
        <p>
          {t`An effective summary has three components: (1) who you are - your title, years of experience, and domain; (2) what you're known for - your signature skill, specialization, or the problem you solve best; and (3) what you bring to this role - a bridge to the job you're targeting.`}
        </p>
        <p>
          {t`Example: "Senior product manager with 8 years in B2B SaaS, specializing in turning ambiguous market opportunities into shipped products with measurable retention impact. Track record of leading 0-to-1 launches across fintech and HR-tech. Eager to bring that lens to a mission-driven team scaling its enterprise tier."`}
        </p>
        <p>
          {t`That's three sentences. It says who (senior PM, 8 years, B2B SaaS), what (0-to-1 launches, retention focus), and why this role (enterprise scaling). It's specific enough to trust but short enough to actually get read.`}
        </p>

        <h2>{t`Length and placement`}</h2>
        <p>
          {t`Two to four sentences is the right length. Any longer and you're eating into the space better used for bullet points that prove your claims. Shorter risks being too generic to be useful.`}
        </p>
        <p>
          {t`Place the summary immediately below your name and contact information - before experience, before skills, before everything else. The reader should know who you are and why you're relevant within the first ten seconds.`}
        </p>

        <h2>{t`Tailoring for each application`}</h2>
        <p>
          {t`Your summary should shift slightly for each application. Keep the core arc - who you are and what you do - but adjust the bridge sentence to reflect the specific role. If you're applying for a role emphasizing "enterprise sales", say so explicitly. If the job description highlights "distributed teams", reference your experience with them.`}
        </p>
        <p>
          {t`This is another area where keyword alignment matters. If the job title in the posting is "Staff Engineer" and you write "Senior Software Engineer" in your summary, some ATS systems will score them as a partial match. Mirror the exact target title when honest to do so.`}
        </p>

        <h2>{t`Common mistakes to fix`}</h2>
        <ul>
          <li>{t`Starting with "I" - write in third-person fragments ("Experienced designer…" not "I am an experienced designer…")`}</li>
          <li>{t`Using passive voice ("Was responsible for…") - use active verbs ("Led", "Built", "Reduced")`}</li>
          <li>{t`Including years of birth, marital status, or a photo in countries where this isn't standard - it adds risk and zero value`}</li>
          <li>{t`Repeating the same summary verbatim across every application - five targeted minutes of editing outperforms a generic one`}</li>
        </ul>

        <h2>{t`A practical exercise`}</h2>
        <p>
          {t`Open the job description for the role you want most right now. Underline every qualification, skill, and responsibility. Then write a summary that honestly reflects your experience against those underlined items. Read it aloud - if it sounds like a brochure, rewrite it until it sounds like a person. That's your starting summary.`}
        </p>
      </>
    ),
  },
  {
    slug: "tailoring-resume-for-each-job",
    title: t`How to Tailor Your Resume for Every Job Application`,
    description: t`Sending the same resume to every job is leaving callbacks on the table. Here's a repeatable system for customizing your resume without starting from scratch each time.`,
    date: "2026-02-28",
    readTime: t`8 min read`,
    tag: t`Strategy`,
    body: (
      <>
        <p>
          {t`The single biggest lever most job seekers have - and consistently fail to pull - is tailoring their resume to each application. Research from hiring platforms consistently shows that tailored applications receive significantly higher callback rates than identical resumes sent to every job. Yet most candidates do the opposite: craft one resume and blast it out.`}
        </p>
        <p>
          {t`The resistance is understandable. Rewriting your resume for every job sounds exhausting. But "tailoring" doesn't mean rebuilding from scratch - it means making targeted, strategic edits that take fifteen to thirty minutes per application once you have a system.`}
        </p>

        <h2>{t`Step 1: Build a master resume`}</h2>
        <p>
          {t`Your master resume is a comprehensive document that includes everything you could ever put on a resume: every role, every accomplishment, every side project, every skill, every certification. It's not meant to be submitted - it's a source of truth you draw from.`}
        </p>
        <p>
          {t`In ResumeLabs, you can create this as your base resume and then duplicate it for each application. Changes to the duplicate don't affect the master. This workflow eliminates the fear of "if I edit this, I'll lose the original".`}
        </p>

        <h2>{t`Step 2: Analyze the job description`}</h2>
        <p>
          {t`Read the job description in three passes. First pass: understand the role - what problem does this company need to solve, and what kind of person solves it? Second pass: highlight hard requirements - specific tools, degrees, years of experience, certifications. Third pass: note the language and framing - do they say "clients" or "customers"? "Develop" or "engineer"? Mirror their vocabulary.`}
        </p>
        <p>
          {t`Create a simple two-column note: "What they need" on one side, "What I have" on the other. This is your tailoring brief.`}
        </p>

        <h2>{t`Step 3: Adjust the summary`}</h2>
        <p>
          {t`Rewrite the bridge sentence in your professional summary to speak directly to this role. If the job emphasizes "leading cross-functional teams at scale", make sure your summary mentions that you've done exactly that. One sentence change in the summary signals to the recruiter that you read the posting carefully - and that already puts you ahead of most candidates.`}
        </p>

        <h2>{t`Step 4: Reorder and trim skills`}</h2>
        <p>
          {t`Move the skills that appear in the job description to the top of your skills section. Remove or deprioritize skills that are irrelevant to this particular role. A skills section with forty items is noise - ten to twelve highly relevant skills is a signal.`}
        </p>

        <h2>{t`Step 5: Rewrite two to three bullet points`}</h2>
        <p>
          {t`This is where the biggest impact sits. For your two or three most recent and relevant roles, rewrite the top one or two bullet points to use the exact language of the job description - where it's honest. If the posting says "reduced time-to-market", and you genuinely did that, use that phrase verbatim.`}
        </p>
        <p>
          {t`Don't fabricate. But do surface relevant facts you might have buried in generic language. "Improved deployment pipeline" becomes "reduced production deployment time by 40% using GitHub Actions CI/CD" - both are true, but the second one directly answers what the job description asked for.`}
        </p>

        <h2>{t`Step 6: Verify before submitting`}</h2>
        <p>
          {t`Before you submit, do one final pass: (1) does the job title in your summary match or closely echo the target title? (2) do the required skills from the posting appear on your resume? (3) is your most relevant experience at the top, not buried halfway down? If you can answer yes to all three, submit with confidence.`}
        </p>

        <h2>{t`Keeping the system sustainable`}</h2>
        <p>
          {t`The candidates who tailor consistently are the ones who treat job searching as a focused sprint rather than a passive numbers game. Set aside dedicated time for each application rather than firing off a dozen resumes in an afternoon. Five thoughtful, tailored applications outperform fifty generic ones - every time.`}
        </p>
        <p>
          {t`ResumeLabs's duplicate-and-edit workflow is designed for exactly this. Your data lives in the cloud, your master resume is always intact, and each tailored version is a named copy you can return to. When the recruiter asks "which version did you send us?", you'll know.`}
        </p>
      </>
    ),
  },
  {
    slug: "resume-length-guide",
    title: t`How Long Should a Resume Be? The Definitive Answer`,
    description: t`One page or two? The honest answer depends on your experience. Here's how to decide the right length and how to cut a resume down without losing what matters.`,
    date: "2026-04-05",
    readTime: t`6 min read`,
    tag: t`Length`,
    body: (
      <>
        <p>
          {t`"Keep it to one page" is the most repeated resume advice on the internet - and it's only half true. Length is not a vanity metric; it's a function of how much relevant experience you have and how much of it a recruiter needs to make a decision. This guide gives you a clear rule and a process for hitting it.`}
        </p>

        <h2>{t`The one-page rule and when it applies`}</h2>
        <p>
          {t`One page is the right target for students, recent graduates, and anyone with fewer than roughly ten years of experience. Recruiters spend an average of six to eight seconds on an initial scan, and a single, well-edited page respects that reality. If everything important fits on one page, use one page.`}
        </p>

        <h2>{t`When two pages is the correct choice`}</h2>
        <p>
          {t`Past ten years of relevant experience, forcing everything onto one page does more harm than good - you end up shrinking fonts and margins to the point of unreadability. Senior professionals, managers, and technical specialists with a deep project history are expected to use two pages. Academic CVs and certain medical, legal, and research roles can run longer still.`}
        </p>
        <p>
          {t`The rule is simple: never pad to fill a second page, and never compress to the point of illegibility to avoid one. Length should follow substance, not the other way around.`}
        </p>

        <h2>{t`What earns a place on the page`}</h2>
        <p>
          {t`Every line competes for space. Ask of each bullet: does this prove I can do the job I'm applying for? Recent, relevant, results-driven content stays. A role from fifteen years ago that no longer reflects your trajectory can be reduced to a single line or dropped entirely.`}
        </p>
        <ul>
          <li>{t`Keep - quantified accomplishments, relevant skills, and roles from the last ten to fifteen years.`}</li>
          <li>{t`Trim - early-career jobs unrelated to your current direction, and duplicated responsibilities across similar roles.`}</li>
          <li>{t`Cut - references ("available on request"), generic objective statements, and obvious skills like "Microsoft Word".`}</li>
        </ul>

        <h2>{t`How to cut a resume down without gutting it`}</h2>
        <p>
          {t`If you're one or two lines over, you rarely need to delete a whole section. Tighten the writing first: remove filler words, combine related bullets, and cut weak openers like "Responsible for". Reducing a three-line bullet to two lines, four times over, often recovers a full page.`}
        </p>
        <p>
          {t`Layout helps too. Margins between 0.5" and 1", a 10–12pt body font, and consolidating your contact details into a single header line all reclaim space without touching content. In ResumeLabs you can adjust typography, spacing, and margins live and watch the page reflow instantly.`}
        </p>

        <h2>{t`The bottom line`}</h2>
        <p>
          {t`Don't optimize for a page count - optimize for relevance, then let the length be whatever the relevant content requires. A focused two-page resume beats a cramped one-pager, and a clean one-pager beats a padded two-pager. Match the length to your experience, and cut everything that doesn't earn its space.`}
        </p>
      </>
    ),
  },
  {
    slug: "resume-format-guide",
    title: t`Chronological, Functional, or Combination: Choosing a Resume Format`,
    description: t`The format you choose decides what a recruiter sees first. Here's how the three main resume formats work, who each one suits, and which to avoid.`,
    date: "2026-04-18",
    readTime: t`8 min read`,
    tag: t`Formatting`,
    body: (
      <>
        <p>
          {t`Before you write a single bullet point, you make a structural decision: which format will your resume follow? The format controls what a recruiter notices first - your career progression, your skills, or a blend of both. Choosing the wrong one can bury your strongest selling point. Here's how the three standard formats compare.`}
        </p>

        <h2>{t`The reverse-chronological format`}</h2>
        <p>
          {t`This is the default, and for good reason. It lists your work history from most recent to oldest, with accomplishments under each role. Recruiters and ATS systems are built around it - they expect to see job titles, employers, and dates in this order.`}
        </p>
        <p>
          {t`Use it if you have a steady work history in a consistent field. It showcases progression and makes promotions and tenure obvious. For the large majority of candidates, this is the correct choice - and the one you should default to unless you have a specific reason not to.`}
        </p>

        <h2>{t`The functional (skills-based) format`}</h2>
        <p>
          {t`The functional format leads with skill categories and accomplishments, pushing the actual work history into a brief list near the bottom. In theory, it lets you foreground capabilities over chronology.`}
        </p>
        <p>
          {t`In practice, recruiters distrust it - because it's the format people reach for to hide something: employment gaps, frequent job changes, or a thin history. ATS parsers also struggle with it, since they expect accomplishments tied to specific roles and dates. We generally recommend against a purely functional resume.`}
        </p>

        <h2>{t`The combination format`}</h2>
        <p>
          {t`The combination (or hybrid) format opens with a compact skills or highlights section, then follows with a full reverse-chronological work history. You get the best of both: relevant skills are visible immediately, but the recruiter still gets the dates and context they need to trust them.`}
        </p>
        <p>
          {t`This is the strongest choice for career changers, senior professionals with a broad skill set, and anyone whose most relevant skills aren't obvious from their most recent job title alone.`}
        </p>

        <h2>{t`Which format fits your situation`}</h2>
        <ul>
          <li>{t`Steady career in one field - reverse-chronological.`}</li>
          <li>{t`Changing industries or roles - combination.`}</li>
          <li>{t`Senior or technical with many specialties - combination.`}</li>
          <li>{t`Employment gaps you're worried about - combination, addressed honestly, not a functional format that tries to hide them.`}</li>
          <li>{t`Recent graduate - reverse-chronological, leading with education and projects.`}</li>
        </ul>

        <h2>{t`Format is structure, not decoration`}</h2>
        <p>
          {t`Whatever format you choose, keep the visual structure clean: consistent headings, clear date alignment, and a single-column layout for the body where possible - multi-column designs can confuse ATS parsers. ResumeLabs's templates handle the structural mechanics for you, so you can switch formats and templates without rebuilding your content from scratch.`}
        </p>
        <p>
          {t`Pick the format that puts your strongest, most relevant evidence in the recruiter's first eight seconds of attention. For most people that's reverse-chronological; for those whose recent title undersells them, it's combination.`}
        </p>
      </>
    ),
  },
  {
    slug: "quantifying-achievements",
    title: t`How to Quantify Your Achievements on a Resume`,
    description: t`"Responsible for managing a team" tells a recruiter nothing. Numbers do. Here's how to turn vague duties into measurable accomplishments that prove impact.`,
    date: "2026-05-02",
    readTime: t`7 min read`,
    tag: t`Writing`,
    body: (
      <>
        <p>
          {t`The difference between a forgettable resume and a compelling one is usually numbers. "Responsible for managing social media" describes a duty. "Grew the brand's Instagram following from 4k to 60k in 14 months, driving a 22% increase in referral traffic" describes an outcome. Recruiters remember outcomes. This guide shows you how to find and write them.`}
        </p>

        <h2>{t`Why numbers work`}</h2>
        <p>
          {t`Quantified achievements do three things at once: they prove the result actually happened, they give the recruiter a sense of the scale you operate at, and they let you be compared favorably against other candidates who only listed responsibilities. A number is a built-in credibility signal - it implies you measured your own impact, which is exactly the mindset employers want.`}
        </p>

        <h2>{t`The four metrics almost any role can use`}</h2>
        <p>
          {t`If you're stuck, anchor your bullets to one of these dimensions:`}
        </p>
        <ul>
          <li>{t`Money - revenue generated, costs reduced, budgets managed, deals closed.`}</li>
          <li>{t`Time - hours saved, deadlines beaten, processes accelerated, time-to-delivery reduced.`}</li>
          <li>{t`Volume - users, customers, transactions, tickets, team size, units shipped.`}</li>
          <li>{t`Percentage change - growth, efficiency gains, error reduction, retention improvement.`}</li>
        </ul>

        <h2>{t`Finding numbers when your job "wasn't numerical"`}</h2>
        <p>
          {t`Many people assume their work can't be quantified. It almost always can. Did you support a team? How many people? Did you handle requests? Roughly how many per week? Did you improve a process? How much faster was it afterward? Even approximate, honestly-estimated figures ("approximately", "over", "~") are far stronger than no figure at all.`}
        </p>
        <p>
          {t`A useful exercise: for each bullet, ask "how much?", "how many?", "how often?", and "what changed as a result?". Answering even one of those four questions usually surfaces a number worth including.`}
        </p>

        <h2>{t`The accomplishment formula`}</h2>
        <p>
          {t`A strong bullet follows a simple pattern: action verb + what you did + the measurable result. "Redesigned the checkout flow, reducing cart abandonment by 18% and adding an estimated $240k in annual revenue." Start with the verb, state the action plainly, and end on the impact. Lead with the result when it's the most impressive part.`}
        </p>

        <h2>{t`Honesty and the recruiter's BS detector`}</h2>
        <p>
          {t`Quantifying is not inflating. Recruiters interview thousands of people and can smell a fabricated metric immediately - and once they catch one exaggeration, they distrust the entire resume. Use real numbers you can defend in an interview. "I estimated that figure from our internal dashboard" is a perfectly good answer; "I made it up" is a disqualifier.`}
        </p>

        <h2>{t`Put it into practice`}</h2>
        <p>
          {t`Open your current resume and find every bullet that starts with "Responsible for", "Helped with", or "Worked on". Those are your weakest lines. Rewrite each one with a verb and a number. You won't be able to quantify all of them - but turning even half of your bullets into measurable accomplishments will transform how your resume reads.`}
        </p>
      </>
    ),
  },
  {
    slug: "cover-letter-guide",
    title: t`How to Write a Cover Letter That Complements Your Resume`,
    description: t`A good cover letter doesn't repeat your resume - it adds the context a list of bullet points can't. Here's a structure that works and the mistakes that get letters skipped.`,
    date: "2026-05-15",
    readTime: t`8 min read`,
    tag: t`Cover Letter`,
    body: (
      <>
        <p>
          {t`Cover letters are often dismissed as obsolete, yet many hiring managers still read them - especially for competitive or senior roles - and use them as a tie-breaker. The mistake most people make is treating the cover letter as a prose version of their resume. Its real job is different: to add the story, motivation, and context that a list of bullet points can't carry.`}
        </p>

        <h2>{t`When a cover letter is worth writing`}</h2>
        <p>
          {t`Always write one when the application explicitly asks for it - skipping it signals you don't follow instructions. Write one when you're changing industries, have an employment gap to explain, or are genuinely excited about a specific company. For bulk applications to roles that don't request one, your energy is usually better spent tailoring the resume. When in doubt, include one.`}
        </p>

        <h2>{t`The structure that works`}</h2>
        <p>{t`Keep it to three or four short paragraphs on a single page:`}</p>
        <ul>
          <li>{t`Opening - name the role and lead with a hook: a specific reason you're a strong fit or a genuine connection to the company. Skip "I am writing to apply for...".`}</li>
          <li>{t`Body (one or two paragraphs) - pick one or two accomplishments that map directly to the job's top requirements, and tell the brief story behind them. This is where you add color the resume can't.`}</li>
          <li>{t`Why this company - show you've researched them. Reference a product, value, or recent development and connect it to what you want to contribute.`}</li>
          <li>{t`Close - a confident, forward-looking line and a thank-you. No groveling, no "I hope to hear from you".`}</li>
        </ul>

        <h2>{t`Don't echo the resume - extend it`}</h2>
        <p>
          {t`If a recruiter can delete your cover letter and lose no information, it failed. The resume says what you did; the cover letter says why it mattered, what you learned, and why you want to do it here next. Use it to explain a pivot, a gap, or a non-obvious connection between your background and the role. That narrative is something a bulleted resume structurally cannot provide.`}
        </p>

        <h2>{t`Common mistakes that get letters skipped`}</h2>
        <ul>
          <li>{t`Generic salutations - find the hiring manager's name where you can; "Dear Hiring Team" beats "To Whom It May Concern".`}</li>
          <li>{t`The wrong company name - the unmistakable sign of a copy-pasted letter. Proofread every time.`}</li>
          <li>{t`Restating the resume line by line - adds nothing and wastes the reader's goodwill.`}</li>
          <li>{t`Making it about you, not them - "this role would help me grow" matters less than what you'll deliver for them.`}</li>
          <li>{t`Length - over one page and most hiring managers stop reading.`}</li>
        </ul>

        <h2>{t`Keep it consistent with your resume`}</h2>
        <p>
          {t`Your cover letter and resume should look and sound like they came from the same person - same name styling, same contact header, same tone. A matching header makes the application feel deliberate and professional. Write the letter after you've tailored the resume, so both are aimed at the same role and reinforce the same handful of strengths.`}
        </p>
      </>
    ),
  },
  {
    slug: "common-resume-mistakes",
    title: t`11 Resume Mistakes That Quietly Cost You Interviews`,
    description: t`Most resumes aren't rejected for one glaring flaw - they're passed over for a handful of small, avoidable mistakes. Here are the most common ones and how to fix each.`,
    date: "2026-05-28",
    readTime: t`7 min read`,
    tag: t`Mistakes`,
    body: (
      <>
        <p>
          {t`Most resumes don't get rejected for one catastrophic error. They get quietly passed over for a handful of small, fixable mistakes that add up to "not quite". The good news: every item on this list takes minutes to correct once you know to look for it.`}
        </p>

        <h2>{t`Writing and content mistakes`}</h2>
        <ul>
          <li>{t`Typos and grammar errors - the single fastest way to get cut. They read as carelessness. Proofread, read it aloud, and have someone else check it.`}</li>
          <li>{t`Listing duties instead of achievements - "Responsible for X" describes the job; "Did X, which produced Y result" describes you. Lead with outcomes and numbers.`}</li>
          <li>{t`A generic, untailored resume - sending the identical document to every posting. Even small role-specific edits dramatically raise your callback odds.`}</li>
          <li>{t`Vague buzzwords with no proof - "results-driven team player" means nothing without evidence. Show the result, don't claim the trait.`}</li>
          <li>{t`Passive voice and weak verbs - "was tasked with" is forgettable. "Led", "built", "reduced", "launched" carry energy.`}</li>
        </ul>

        <h2>{t`Formatting and layout mistakes`}</h2>
        <ul>
          <li>{t`ATS-hostile design - text in images, headers, tables, or multi-column layouts can be misread or skipped by parsers. Keep the structure clean and text-based.`}</li>
          <li>{t`Inconsistent formatting - dates, fonts, bullet styles, and spacing that vary across the page look unpolished. Pick one system and apply it everywhere.`}</li>
          <li>{t`Walls of text - dense paragraphs don't get read. Use concise bullets, white space, and clear section breaks.`}</li>
        </ul>

        <h2>{t`Strategic mistakes`}</h2>
        <ul>
          <li>{t`Including outdated or irrelevant information - a 1998 graduation year, an objective statement, or "references available on request" just take up space. Cut them.`}</li>
          <li>{t`An unprofessional email address - use a simple firstname.lastname address, not the one you made in high school.`}</li>
          <li>{t`Burying your best material - if your strongest accomplishment is in the third bullet of your second job, a six-second scan will miss it. Front-load impact.`}</li>
        </ul>

        <h2>{t`Run the five-minute audit`}</h2>
        <p>
          {t`Before you submit, do a quick pass against this list: spelling, achievements over duties, tailored to the role, clean and ATS-friendly layout, nothing outdated, best material up top. Most candidates never run this check - which is exactly why running it puts you ahead.`}
        </p>
        <p>
          {t`Building your resume in ResumeLabs removes a whole category of these errors automatically: consistent formatting, ATS-friendly templates, and clean structure are handled for you, so you can spend your time on the content that actually wins interviews.`}
        </p>
      </>
    ),
  },
  {
    slug: "career-change-resume",
    title: t`How to Write a Resume for a Career Change`,
    description: t`Switching fields means your job titles no longer tell your story. Here's how to reframe your experience around transferable skills so recruiters see the fit.`,
    date: "2026-06-10",
    readTime: t`8 min read`,
    tag: t`Career Change`,
    body: (
      <>
        <p>
          {t`Changing careers is one of the hardest resume challenges, because your job titles no longer do the selling for you. A recruiter scanning for "marketing manager" won't stop on "high school teacher" - even though teaching built exactly the communication, project management, and stakeholder skills the role needs. Your job is to make that relevance impossible to miss.`}
        </p>

        <h2>{t`Start by decoding the target role`}</h2>
        <p>
          {t`Before touching your own history, study three or four job descriptions for the role you want. List the skills and responsibilities they repeat. That list is your translation key - it tells you which parts of your past experience to surface and what language to describe them in. You're not inventing new experience; you're re-framing real experience in the vocabulary of the new field.`}
        </p>

        <h2>{t`Lead with a combination format`}</h2>
        <p>
          {t`Career changers benefit most from a combination resume: open with a short summary and a transferable-skills section, then follow with your reverse-chronological history. This puts your relevant capabilities in the recruiter's first few seconds, before they reach job titles that don't obviously match. It addresses the "but they've never done this" reaction head-on.`}
        </p>

        <h2>{t`Write a bridge summary`}</h2>
        <p>
          {t`Your professional summary is where you explicitly connect past to future. Name your transition and frame it as an asset: "Operations lead with 7 years coordinating cross-functional teams, transitioning into product management - bringing a delivery-focused, stakeholder-first lens to building software." This tells the recruiter what you're doing and why your background is a feature, not a detour.`}
        </p>

        <h2>{t`Translate accomplishments into the new field's language`}</h2>
        <p>
          {t`This is the core of the work. Take each accomplishment and rewrite it using the terms from your target job descriptions, while keeping it truthful:`}
        </p>
        <ul>
          <li>{t`"Managed a classroom of 30 students" becomes "Led and engaged a group of 30 daily, adapting communication to diverse needs" for a customer-facing or training role.`}</li>
          <li>{t`"Planned the annual fundraiser" becomes "Owned end-to-end project planning and budgeting for a $40k event with 12 stakeholders" for a project management role.`}</li>
          <li>{t`"Tracked patient records" becomes "Maintained data accuracy and compliance across hundreds of records under strict privacy requirements" for an operations or data role.`}</li>
        </ul>

        <h2>{t`Show you've started the transition`}</h2>
        <p>
          {t`Recruiters worry a career changer is just curious, not committed. Defuse that with evidence: relevant courses, certifications, side projects, freelance work, or volunteer roles in the new field. Even a small, self-directed project signals that the switch is deliberate and already underway. Give these their own section if you have enough to show.`}
        </p>

        <h2>{t`Don't apologize for the pivot`}</h2>
        <p>
          {t`Frame your background as a differentiator, not a deficiency. The candidate who came to UX from nursing brings empathy and high-stakes decision-making that a typical applicant doesn't. The teacher entering sales brings communication and resilience. Your unconventional path is a story - tell it with confidence, and let the resume prove the skills are real. With ResumeLabs you can keep your original-field resume intact and build a re-framed version alongside it, so you're never starting from a blank page.`}
        </p>
      </>
    ),
  },
  {
    slug: "skills-section-guide",
    title: t`How to Write the Skills Section of Your Resume`,
    description: t`The skills section is prime ATS territory and the easiest part to get wrong. Here's what to include, what to leave out, and how to organize it so it actually helps.`,
    date: "2026-06-18",
    readTime: t`6 min read`,
    tag: t`Skills`,
    body: (
      <>
        <p>
          {t`The skills section is small but disproportionately important: it's where ATS systems hunt for keyword matches, and where a recruiter glances to confirm you have the basics before reading further. Done well, it's a fast credibility check. Done badly, it's a forty-item list of noise that proves nothing.`}
        </p>

        <h2>{t`Hard skills vs soft skills`}</h2>
        <p>
          {t`Hard skills are specific, teachable, and verifiable: programming languages, tools, certifications, languages spoken, techniques. Soft skills are interpersonal traits: communication, leadership, adaptability. The skills section should be dominated by hard skills - they're what ATS systems match and what recruiters can act on. Soft skills are better demonstrated inside your experience bullets than asserted in a list.`}
        </p>

        <h2>{t`Pull your skills from the job description`}</h2>
        <p>
          {t`The most effective skills section mirrors the language of the posting. Read the job description, note every tool, technology, and qualification it names, and - where you genuinely have them - list them using the exact same wording. If the posting says "PostgreSQL", write "PostgreSQL", not "SQL databases". Exact matches are what the parser scores.`}
        </p>

        <h2>{t`Organize for scannability`}</h2>
        <p>
          {t`A flat list of thirty skills is hard to read. Group related skills under short subheadings - for example "Languages", "Frameworks", "Tools", "Design" - so a recruiter can find what they're looking for in a glance. Order the groups and the items within them by relevance to the target role, most relevant first.`}
        </p>

        <h2>{t`Be honest about proficiency`}</h2>
        <p>
          {t`Only list skills you'd be comfortable being interviewed on. Listing a language or tool you touched once is a fast way to get exposed in a technical screen. If you want to signal depth, a simple proficiency indicator (e.g. "Spanish - fluent", "Python - advanced") is more useful than a graphical skill bar, which conveys no real information and can confuse ATS parsers.`}
        </p>

        <h2>{t`What to leave out`}</h2>
        <ul>
          <li>{t`Obvious baseline skills - "email", "Microsoft Word", "internet research" signal inexperience more than competence.`}</li>
          <li>{t`Skill-rating graphics and star bars - they take space, mean different things to different readers, and don't parse well.`}</li>
          <li>{t`Skills irrelevant to the role - a long tail of unrelated tools dilutes the relevant ones.`}</li>
          <li>{t`Soft-skill clichés - "hard worker", "team player" belong nowhere on a modern resume.`}</li>
        </ul>

        <h2>{t`Keep it tight`}</h2>
        <p>
          {t`Aim for roughly ten to fifteen genuinely relevant skills, organized into a few clear groups, mirroring the job description's language. That's enough to clear the ATS keyword check and reassure a recruiter, without burying your strongest qualifications under a pile of filler. Quality and relevance beat quantity every time.`}
        </p>
      </>
    ),
  },
];
